/**
 * Fase 8 — Assistente de IA: recursos, limites de texto por posicionamento, políticas de anúncio
 * da Meta (checagem local), créditos/custo por chamada, schemas Zod das entradas e das saídas
 * estruturadas (variações, estrutura de campanha, regras sugeridas) e DTOs da API.
 *
 * Regra de ouro: a IA sugere, o usuário aprova. Nada aqui publica, ativa, pausa ou muda orçamento.
 */
import { z } from "zod";
import { CAMPAIGN_OBJECTIVES, CAMPAIGN_LEVELS } from "./campaign-enums.js";
import { CALL_TO_ACTIONS } from "./creation-enums.js";
import { periodQuerySchema } from "./reports-schemas.js";
import {
  RULE_ACTION_TYPES,
  RULE_METRICS,
  RULE_OPERATORS,
  RULE_WINDOWS,
  ruleBodySchema,
  type RuleBody,
} from "./rules.js";

// ---- Recursos e modelos ----------------------------------------------------------------------------

export const AI_RESOURCES = ["variacoes", "analise", "diagnostico", "estrutura", "regras"] as const;
export type AiResource = (typeof AI_RESOURCES)[number];

export const AI_RESOURCE_LABELS: Record<AiResource, string> = {
  variacoes: "Variações de anúncio",
  analise: "Análise (chat)",
  diagnostico: "Diagnóstico rápido",
  estrutura: "Estrutura de campanha",
  regras: "Regras sugeridas",
};

export const AI_MODES = ["mock", "live"] as const;
export type AiMode = (typeof AI_MODES)[number];

/** Modelos padrão (configuráveis por ANTHROPIC_MODEL, ANTHROPIC_MODEL_FAST e ANTHROPIC_MODEL_DEEP). */
export const AI_DEFAULT_MODELS = {
  default: "claude-sonnet-5-5",
  fast: "claude-haiku-5-5",
  deep: "claude-opus-5-5",
} as const;

/** Preço em dólares por milhão de tokens (tabela pública da Anthropic, conferida em 19/10/2026). */
export interface AiModelPrice {
  inputUsdPerMTok: number;
  outputUsdPerMTok: number;
}

export const AI_MODEL_PRICES: Record<string, AiModelPrice> = {
  "claude-opus-5-5": { inputUsdPerMTok: 4, outputUsdPerMTok: 20 },
  "claude-opus-5": { inputUsdPerMTok: 5, outputUsdPerMTok: 25 },
  "claude-sonnet-5-5": { inputUsdPerMTok: 2, outputUsdPerMTok: 10 },
  "claude-sonnet-5": { inputUsdPerMTok: 2, outputUsdPerMTok: 10 },
  "claude-haiku-5-5": { inputUsdPerMTok: 0.1, outputUsdPerMTok: 0.5 },
  "claude-haiku-4-5": { inputUsdPerMTok: 1, outputUsdPerMTok: 5 },
};

/** Preço usado quando o modelo não está na tabela (o mais caro conhecido, para não subestimar). */
export const AI_FALLBACK_PRICE: AiModelPrice = { inputUsdPerMTok: 5, outputUsdPerMTok: 25 };

export interface AiUsageTokens {
  inputTokens: number;
  outputTokens: number;
  /** Tokens lidos do cache de prompt (cobrados a 10% da entrada). */
  cacheReadTokens: number;
  /** Tokens gravados no cache (cobrados a 125% da entrada). */
  cacheWriteTokens: number;
}

export const EMPTY_AI_USAGE: AiUsageTokens = {
  inputTokens: 0,
  outputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
};

/** Custo estimado em micro-dólares (1 USD = 1.000.000), inteiro. */
export function estimateCostUsdMicros(model: string, usage: AiUsageTokens): number {
  const price = AI_MODEL_PRICES[model] ?? AI_FALLBACK_PRICE;
  const perTokIn = price.inputUsdPerMTok; // USD por 1M tokens = micro-USD por token
  const perTokOut = price.outputUsdPerMTok;
  const micros =
    usage.inputTokens * perTokIn +
    usage.cacheWriteTokens * perTokIn * 1.25 +
    usage.cacheReadTokens * perTokIn * 0.1 +
    usage.outputTokens * perTokOut;
  return Math.round(micros);
}

/**
 * Créditos de IA: 1 crédito = 1.000 tokens processados (entrada + cache gravado + saída; leitura
 * de cache conta 10%), mínimo 1 por chamada. É a unidade que a Fase 9 vai ligar aos planos.
 */
export const AI_TOKENS_PER_CREDIT = 1000;

export function creditsForUsage(usage: AiUsageTokens): number {
  const weighted =
    usage.inputTokens +
    usage.cacheWriteTokens +
    usage.cacheReadTokens * 0.1 +
    usage.outputTokens;
  return Math.max(1, Math.ceil(weighted / AI_TOKENS_PER_CREDIT));
}

/** Estimativa grosseira de tokens de um texto (≈ 4 caracteres por token), usada pelo modo simulado. */
export function estimateTokens(text: string): number {
  return Math.max(1, Math.ceil(text.length / 4));
}

/** Cota mensal padrão de créditos por workspace (até a Fase 9 ligar aos planos). */
export const AI_DEFAULT_MONTHLY_CREDITS = 500;

// ---- Limites de texto por posicionamento -----------------------------------------------------------------

/** Limites RECOMENDADOS pela Meta (acima disso o texto é cortado com "… ver mais"). */
export const AD_TEXT_RECOMMENDED = { primaryText: 125, headline: 40, description: 30 } as const;
/** Limites MÁXIMOS aceitos pelo produto (o assistente de criação valida de novo ao publicar). */
export const AD_TEXT_MAX = { primaryText: 500, headline: 255, description: 255 } as const;

export interface AdPlacementLimit {
  id: string;
  label: string;
  primaryText: number;
  headline: number | null;
  description: number | null;
}

export const AD_PLACEMENT_LIMITS: readonly AdPlacementLimit[] = [
  { id: "feed", label: "Feed (Facebook/Instagram)", primaryText: 125, headline: 40, description: 30 },
  { id: "stories_reels", label: "Stories e Reels", primaryText: 125, headline: null, description: null },
  { id: "right_column", label: "Coluna da direita", primaryText: 125, headline: 40, description: null },
  { id: "explore", label: "Instagram Explorar", primaryText: 125, headline: null, description: null },
];

export interface AdTextFields {
  primaryText: string;
  headline: string;
  description: string;
}

/** Avisos de corte por posicionamento (não bloqueiam: a pessoa decide). */
export function checkAdTextLimits(ad: AdTextFields): string[] {
  const out: string[] = [];
  for (const p of AD_PLACEMENT_LIMITS) {
    if (ad.primaryText.length > p.primaryText)
      out.push(`${p.label}: texto principal com ${ad.primaryText.length} caracteres será cortado após ${p.primaryText}.`);
    if (p.headline !== null && ad.headline.length > p.headline)
      out.push(`${p.label}: título com ${ad.headline.length} caracteres será cortado após ${p.headline}.`);
    if (p.description !== null && ad.description.length > p.description)
      out.push(`${p.label}: descrição com ${ad.description.length} caracteres será cortada após ${p.description}.`);
  }
  return [...new Set(out)];
}

// ---- Políticas de anúncio (checagem local) -------------------------------------------------------------------

export interface AdPolicyIssue {
  code: string;
  message: string;
}

/**
 * Padrões que violam as políticas de anúncios da Meta: atributos pessoais ("você está acima do
 * peso?"), promessas de resultado garantido, alegações de saúde/dinheiro, urgência enganosa.
 * Conservador de propósito: o texto é só marcado; a pessoa decide.
 */
const POLICY_PATTERNS: { code: string; re: RegExp; message: string }[] = [
  {
    code: "atributo_pessoal",
    re: /\b(voc[eê]|vc)\s+(est[aá]|tem|sofre|[eé])\s+(acima do peso|gord[oa]|obes[oa]|deprimid[oa]|endividad[oa]|falid[oa]|solteir[oa]|doente|careca|velh[oa]|diab[eé]tic[oa])/i,
    message: "Atributo pessoal: não afirme nem insinue características da pessoa (peso, saúde, finanças, estado civil).",
  },
  {
    code: "atributo_pessoal",
    re: /\b(cansad[oa] de (ser|estar) (gord[oa]|pobre|solteir[oa]|careca))/i,
    message: "Atributo pessoal: não afirme nem insinue características da pessoa.",
  },
  {
    code: "promessa_garantida",
    re: /\b(garantid[oa]s?|100%\s*(de\s*)?(resultado|garantia|sucesso)|resultado garantido|sem risco nenhum)\b/i,
    message: "Promessa garantida: a Meta não permite garantir resultados.",
  },
  {
    code: "promessa_saude",
    re: /\b(perca|emagre[cç]a|elimine)\s+(at[eé]\s+)?\d+\s*(kg|quilos)/i,
    message: "Alegação de saúde: não prometa perda de peso específica.",
  },
  {
    code: "promessa_saude",
    re: /\b(cura|curamos|cura definitiva|milagros[oa])\b/i,
    message: "Alegação de saúde: não prometa cura.",
  },
  {
    code: "promessa_financeira",
    re: /\b(fique rico|dinheiro f[aá]cil|renda garantida|ganhe\s+r?\$?\s*\d[\d.]*\s*(por|em)\s+(dia|semana|m[eê]s))\b/i,
    message: "Promessa financeira: não prometa ganhos específicos ou garantidos.",
  },
  {
    code: "antes_depois",
    re: /\bantes e depois\b/i,
    message: "Imagens/alegações de antes e depois são restritas pela Meta.",
  },
];

export function findPolicyIssues(text: string): AdPolicyIssue[] {
  const out: AdPolicyIssue[] = [];
  for (const p of POLICY_PATTERNS) {
    if (p.re.test(text)) out.push({ code: p.code, message: p.message });
  }
  const seen = new Set<string>();
  return out.filter((i) => {
    const k = `${i.code}:${i.message}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// ---- Redação de dados sensíveis -----------------------------------------------------------------------------

const EMAIL_RE = /[\w.+-]+@[\w-]+(\.[\w-]+)+/g;
/** Telefones BR e internacionais com 8+ dígitos (com ou sem +55, parênteses, espaços e hífens). */
const PHONE_RE = /(\+?\d{1,3}[\s-]?)?(\(?\d{2,3}\)?[\s-]?)?\d{4,5}[\s-]?\d{4}\b/g;
const TOKEN_RE = /\b(EAA[A-Za-z0-9]{20,}|sk-ant-[A-Za-z0-9_-]{10,}|MOCK\.[A-Za-z0-9._-]{4,})\b/g;

/** Remove e-mails, telefones e tokens de qualquer texto que vá para a IA. */
export function redactSensitive(text: string): string {
  return text
    .replace(TOKEN_RE, "[token removido]")
    .replace(EMAIL_RE, "[e-mail removido]")
    .replace(PHONE_RE, (m) => (/\d{8,}/.test(m.replace(/\D/g, "")) ? "[telefone removido]" : m));
}

// ---- Contexto de análise (conversa) -------------------------------------------------------------------------

const ACT_RE = /^act_\d{1,30}$/;

export const aiContextSchema = periodQuerySchema.extend({
  clientId: z.uuid().optional(),
  /** Contas específicas (act_...). */
  accounts: z.array(z.string().regex(ACT_RE)).max(50).optional(),
});
export type AiContextInput = z.infer<typeof aiContextSchema>;

export const AI_CONVERSATION_KINDS = ["analise", "diagnostico"] as const;
export type AiConversationKind = (typeof AI_CONVERSATION_KINDS)[number];

export const createConversationSchema = z.object({
  kind: z.literal("analise").default("analise"),
  title: z.string().trim().max(120).optional(),
  context: aiContextSchema.default({}),
});
export type CreateConversationInput = z.input<typeof createConversationSchema>;

export const sendMessageSchema = z.object({
  content: z.string().trim().min(1, "Escreva a pergunta.").max(4000, "Pergunta longa demais (máx. 4.000 caracteres)."),
  /** Análise aprofundada: usa o modelo "deep" (ANTHROPIC_MODEL_DEEP). */
  deep: z.boolean().default(false),
});
export type SendMessageInput = z.input<typeof sendMessageSchema>;

export const diagnoseSchema = periodQuerySchema.extend({
  level: z.enum(CAMPAIGN_LEVELS),
  metaId: z.string().trim().regex(/^\d{1,30}$/, "Informe um id numérico da Meta."),
});
export type DiagnoseInput = z.input<typeof diagnoseSchema>;

// ---- Variações de anúncio -----------------------------------------------------------------------------------

export const AI_TONES = ["direto", "amigavel", "profissional", "urgente", "inspirador"] as const;
export type AiTone = (typeof AI_TONES)[number];
export const AI_TONE_LABELS: Record<AiTone, string> = {
  direto: "Direto",
  amigavel: "Amigável",
  profissional: "Profissional",
  urgente: "Urgente (com oferta)",
  inspirador: "Inspirador",
};

export const generateVariationsSchema = z.object({
  product: z.string().trim().min(3, "Descreva o produto ou serviço.").max(500),
  audience: z.string().trim().max(500).default(""),
  objective: z.enum(CAMPAIGN_OBJECTIVES).default("OUTCOME_SALES"),
  tone: z.enum(AI_TONES).default("direto"),
  offer: z.string().trim().max(300).default(""),
  /** Página de destino (só para contextualizar; nunca é visitada). */
  link: z.string().trim().max(2000).default(""),
  count: z.number().int().min(1).max(6).default(3),
  /** Pistas extras (diferenciais, restrições, palavras a evitar). */
  notes: z.string().trim().max(1000).default(""),
});
export type GenerateVariationsInput = z.input<typeof generateVariationsSchema>;
export type GenerateVariationsBody = z.output<typeof generateVariationsSchema>;

/** Saída estruturada (schema "solto" para o JSON schema do modelo; limites conferidos depois). */
export const adVariationOutputSchema = z.object({
  primaryText: z.string().describe("Texto principal do anúncio, em pt-BR, até 125 caracteres."),
  headline: z.string().describe("Título curto, até 40 caracteres."),
  description: z.string().describe("Descrição, até 30 caracteres."),
  cta: z.enum(CALL_TO_ACTIONS).describe("Botão sugerido (enum da Meta)."),
  angle: z.string().describe("Ângulo/ideia desta variação em poucas palavras."),
});
export const adVariationsOutputSchema = z.object({
  variations: z.array(adVariationOutputSchema),
});
export type AdVariationOutput = z.infer<typeof adVariationOutputSchema>;

export interface AiVariationDto extends AdVariationOutput {
  /** Avisos de corte por posicionamento. */
  limitWarnings: string[];
  /** Possíveis violações de política (a pessoa decide). */
  policyIssues: AdPolicyIssue[];
}

export interface AiVariationsDto {
  variations: AiVariationDto[];
  model: string;
  credits: number;
}

/** Conferência local de uma variação: corta o que passa do máximo e marca limites/políticas. */
export function reviewVariation(v: AdVariationOutput): AiVariationDto {
  const clipped: AdVariationOutput = {
    ...v,
    primaryText: v.primaryText.trim().slice(0, AD_TEXT_MAX.primaryText),
    headline: v.headline.trim().slice(0, AD_TEXT_MAX.headline),
    description: v.description.trim().slice(0, AD_TEXT_MAX.description),
  };
  return {
    ...clipped,
    limitWarnings: checkAdTextLimits(clipped),
    policyIssues: findPolicyIssues(`${clipped.primaryText}\n${clipped.headline}\n${clipped.description}`),
  };
}

// ---- Estrutura de campanha (briefing) ---------------------------------------------------------------------

export const structureBriefingSchema = z.object({
  adAccountMetaId: z.string().regex(ACT_RE, "Escolha a conta de anúncio."),
  business: z.string().trim().min(3, "Descreva o negócio.").max(500),
  offer: z.string().trim().max(500).default(""),
  region: z.string().trim().min(2, "Informe a região (cidade, estado ou país).").max(200),
  /** Orçamento diário total, em centavos da moeda da conta. */
  dailyBudgetCents: z.number().int().min(100, "Informe o orçamento diário.").max(1_000_000_000),
  objective: z.enum(CAMPAIGN_OBJECTIVES).default("OUTCOME_LEADS"),
  notes: z.string().trim().max(1000).default(""),
});
export type StructureBriefingInput = z.input<typeof structureBriefingSchema>;
export type StructureBriefingBody = z.output<typeof structureBriefingSchema>;

export const AI_GENDER_OPTIONS = ["todos", "homens", "mulheres"] as const;

export const structureAdsetOutputSchema = z.object({
  name: z.string().describe("Nome do conjunto de anúncios."),
  rationale: z.string().describe("Por que este conjunto existe (1 frase)."),
  dailyBudgetCents: z.number().describe("Orçamento diário do conjunto em centavos (só no modo ABO)."),
  ageMin: z.number().describe("Idade mínima (13–65)."),
  ageMax: z.number().describe("Idade máxima (13–65)."),
  genders: z.enum(AI_GENDER_OPTIONS),
  locations: z.array(z.string()).describe("Cidades, estados ou países em texto (ex.: 'São Paulo, SP')."),
  interests: z.array(z.string()).describe("Interesses em texto (serão procurados na busca de segmentação da Meta)."),
  advantageAudience: z.boolean().describe("Deixar a Meta ampliar o público (Advantage+)."),
  adCount: z.number().describe("Quantos anúncios criar neste conjunto (1–5)."),
});

export const structureAudienceOutputSchema = z.object({
  name: z.string(),
  kind: z.enum(["personalizado", "semelhante", "salvo"]),
  description: z.string().describe("Como montar este público (fonte, regra, porcentagem)."),
});

export const structureOutputSchema = z.object({
  campaignName: z.string(),
  objective: z.enum(CAMPAIGN_OBJECTIVES),
  budgetMode: z.enum(["CBO", "ABO"]).describe("CBO = orçamento na campanha; ABO = por conjunto."),
  adsets: z.array(structureAdsetOutputSchema),
  audiences: z.array(structureAudienceOutputSchema),
  notes: z.array(z.string()).describe("Observações e próximos passos."),
});
export type AiStructureOutput = z.infer<typeof structureOutputSchema>;

/** Versão estrita (limites) usada ao validar a saída do modelo e ao criar o rascunho. */
export const structureStrictSchema = structureOutputSchema.extend({
  campaignName: z.string().trim().min(1).max(200),
  adsets: z
    .array(
      structureAdsetOutputSchema.extend({
        name: z.string().trim().min(1).max(200),
        dailyBudgetCents: z.number().int().nonnegative().max(1_000_000_000),
        ageMin: z.number().int().min(13).max(65),
        ageMax: z.number().int().min(13).max(65),
        locations: z.array(z.string().trim().min(1).max(100)).max(20),
        interests: z.array(z.string().trim().min(1).max(100)).max(20),
        adCount: z.number().int().min(1).max(5),
      }),
    )
    .min(1)
    .max(10),
  audiences: z.array(structureAudienceOutputSchema).max(10),
  notes: z.array(z.string().max(500)).max(20),
});

export interface AiStructureDto {
  structure: AiStructureOutput;
  model: string;
  credits: number;
}

export const createDraftFromStructureSchema = z.object({
  adAccountMetaId: z.string().regex(ACT_RE),
  structure: structureStrictSchema,
});
export type CreateDraftFromStructureInput = z.input<typeof createDraftFromStructureSchema>;

export interface AiDraftFromStructureDto {
  draftId: string;
  /** Localizações e interesses que não foram encontrados na busca da Meta (a pessoa completa). */
  unresolved: { kind: "localizacao" | "interesse"; adsetName: string; term: string }[];
}

// ---- Regras sugeridas ---------------------------------------------------------------------------------------

export const suggestRulesSchema = z.object({
  clientId: z.uuid("Escolha o cliente."),
});
export type SuggestRulesInput = z.input<typeof suggestRulesSchema>;

export const ruleSuggestionOutputSchema = z.object({
  name: z.string().describe("Nome curto da regra."),
  description: z.string().describe("O que a regra faz e por quê, em 1–2 frases."),
  level: z.enum(CAMPAIGN_LEVELS),
  groups: z.array(
    z.object({
      conditions: z.array(
        z.object({
          metric: z.enum(RULE_METRICS),
          operator: z.enum(RULE_OPERATORS),
          value: z.number().describe("Na unidade da tela: moeda (não centavos), % para CTR, razão para ROAS."),
          value2: z.number().nullable().describe("Só para 'entre'."),
          window: z.enum(RULE_WINDOWS),
        }),
      ),
    }),
  ),
  actions: z.array(
    z.object({
      type: z.enum(RULE_ACTION_TYPES),
      mode: z.enum(["percentual", "valor"]).nullable(),
      value: z.number().nullable(),
    }),
  ),
  maxBudget: z.number().nullable().describe("Teto de segurança na moeda da conta."),
  minBudget: z.number().nullable(),
  frequencyMinutes: z.enum(["15", "30", "60"]),
});
export const ruleSuggestionsOutputSchema = z.object({
  suggestions: z.array(ruleSuggestionOutputSchema),
});
export type RuleSuggestionOutput = z.infer<typeof ruleSuggestionOutputSchema>;

export interface AiRuleSuggestionDto extends RuleSuggestionOutput {
  /** Corpo pronto para `POST /rules` (desativada), já validado pelo schema das regras. */
  rule: RuleBody;
  /** Resumo "SE … ENTÃO …". */
  summary: string;
}

export interface AiRuleSuggestionsDto {
  clientId: string;
  clientName: string;
  adAccountMetaIds: string[];
  suggestions: AiRuleSuggestionDto[];
  model: string;
  credits: number;
  /** Avisos (ex.: cliente sem metas definidas). */
  warnings: string[];
}

/** Converte uma sugestão em corpo de regra (sempre DESATIVADA). Lança se o schema das regras recusar. */
export function ruleBodyFromSuggestion(
  s: RuleSuggestionOutput,
  scope: { clientId: string; adAccountMetaIds: string[] },
): RuleBody {
  return ruleBodySchema.parse({
    name: s.name,
    clientId: scope.clientId,
    active: false,
    scope: { level: s.level, adAccountMetaIds: scope.adAccountMetaIds, selection: { mode: "nome" } },
    groups: s.groups.map((g) => ({
      conditions: g.conditions.map((c) => ({
        metric: c.metric,
        operator: c.operator,
        value: c.value,
        ...(c.operator === "entre" && c.value2 !== null ? { value2: c.value2 } : {}),
        window: c.window,
      })),
    })),
    actions: s.actions.map((a) => ({
      type: a.type,
      ...(a.mode !== null ? { mode: a.mode } : {}),
      ...(a.value !== null ? { value: a.value } : {}),
    })),
    safety: {
      maxBudget: s.maxBudget,
      minBudget: s.minBudget,
      maxActionsPerItemPerDay: 1,
      cooldownMinutes: 1440,
    },
    frequencyMinutes: Number(s.frequencyMinutes),
  });
}

export const createSuggestedRuleSchema = z.object({
  clientId: z.uuid(),
  suggestion: ruleSuggestionOutputSchema,
});
export type CreateSuggestedRuleInput = z.input<typeof createSuggestedRuleSchema>;

// ---- DTOs de status, uso e conversas -------------------------------------------------------------------------

export interface AiQuotaDto {
  monthlyCredits: number;
  usedCredits: number;
  remainingCredits: number;
  /** Mês corrente (AAAA-MM) e quando a cota zera. */
  month: string;
  resetsAt: string;
}

export interface AiStatusDto {
  mode: AiMode;
  /** No modo live sem chave, a interface avisa antes de tentar. */
  configured: boolean;
  models: { default: string; fast: string; deep: string };
  quota: AiQuotaDto;
  /** O papel pode gerar conteúdo (variações, estrutura, regras)? Analista só analisa. */
  canWrite: boolean;
}

export interface AiUsageByResourceDto {
  resource: AiResource;
  label: string;
  calls: number;
  credits: number;
  inputTokens: number;
  outputTokens: number;
  costUsdMicros: number;
}

export interface AiUsageEntryDto {
  id: string;
  resource: AiResource;
  model: string;
  credits: number;
  inputTokens: number;
  outputTokens: number;
  costUsdMicros: number;
  userName: string | null;
  createdAt: string;
}

export interface AiUsageDto {
  month: string;
  quota: AiQuotaDto;
  byResource: AiUsageByResourceDto[];
  totalCalls: number;
  totalCredits: number;
  totalCostUsdMicros: number;
  recent: AiUsageEntryDto[];
}

export interface AiConversationDto {
  id: string;
  kind: AiConversationKind;
  title: string;
  context: AiContextInput & { level?: string; metaId?: string };
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
}

export interface AiMessageDto {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Resposta cortada pelo usuário ("parar geração"). */
  interrupted: boolean;
  credits: number | null;
  createdAt: string;
}

export interface AiConversationDetailDto extends AiConversationDto {
  messages: AiMessageDto[];
}

/** Eventos do SSE que o navegador recebe ao conversar/diagnosticar. */
export type AiStreamEvent =
  | { type: "start"; conversationId: string; messageId: string; userMessageId: string }
  | { type: "delta"; text: string }
  | { type: "done"; messageId: string; credits: number; remainingCredits: number; interrupted: boolean }
  | { type: "error"; code: string; message: string };

export const aiUsageQuerySchema = z.object({
  /** AAAA-MM; padrão = mês corrente. */
  month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
});
